// Cascading Campus → Building → Room picker, shared by the Facilities work-order
// form and the Tickets create/detail forms. Both surfaces store CODES from the
// same tenant-configured cascade (Settings → Facilities → Locations). Choosing a
// Campus filters the Building list; choosing a Building filters the Room list.
// Legacy free-text values that predate the cascade still render via a fallback
// option so nothing disappears on existing records.

import { getPlaces } from "../lib/api";
import { useApi } from "../lib/useApi";

export interface PlaceValue {
  campus: string;
  building: string;
  room: string;
}

export function LocationPicker({
  campus,
  building,
  room,
  onChange,
  disabled,
  layout = "row",
}: {
  campus: string;
  building: string;
  room: string;
  /** Emits the next triple; changing a parent clears now-orphaned descendants. */
  onChange: (next: PlaceValue) => void;
  disabled?: boolean;
  /** "row" wraps the fields in a `.row`; "stack" renders them bare. */
  layout?: "row" | "stack";
}) {
  const { data, loading } = useApi((signal) => getPlaces(signal), []);
  const campuses = data?.campuses ?? [];
  const buildings = data?.buildings ?? [];
  const rooms = data?.rooms ?? [];

  const buildingsForCampus = buildings.filter((b) => b.campus_code === campus);
  const roomsForBuilding = rooms.filter((r) => r.building_code === building);

  function chooseCampus(c: string) {
    // Keep building only if it still belongs to the new campus; keep room only
    // if its building survives.
    const keepBuilding = buildings.some((b) => b.code === building && b.campus_code === c);
    const nextBuilding = keepBuilding ? building : "";
    const keepRoom = keepBuilding && rooms.some((r) => r.code === room && r.building_code === nextBuilding);
    onChange({ campus: c, building: nextBuilding, room: keepRoom ? room : "" });
  }
  function chooseBuilding(b: string) {
    const keepRoom = rooms.some((r) => r.code === room && r.building_code === b);
    onChange({ campus, building: b, room: keepRoom ? room : "" });
  }
  function chooseRoom(r: string) {
    onChange({ campus, building, room: r });
  }

  // Fallbacks so a stored value not (or no longer) in the lists still shows.
  const campusOrphan = campus && !campuses.some((c) => c.code === campus);
  const buildingOrphan = building && !buildingsForCampus.some((b) => b.code === building);
  const roomOrphan = room && !roomsForBuilding.some((r) => r.code === room);

  const campusField = (
    <label className="field">
      <span className="label">Campus</span>
      <select value={campus} onChange={(e) => chooseCampus(e.target.value)} disabled={disabled || loading}>
        <option value="">—</option>
        {campuses.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        ))}
        {campusOrphan ? <option value={campus}>{campus}</option> : null}
      </select>
    </label>
  );
  const buildingField = (
    <label className="field">
      <span className="label">Building</span>
      <select
        value={building}
        onChange={(e) => chooseBuilding(e.target.value)}
        disabled={disabled || loading || (!campus && !buildingOrphan)}
      >
        <option value="">—</option>
        {buildingsForCampus.map((b) => (
          <option key={b.code} value={b.code}>
            {b.label}
          </option>
        ))}
        {buildingOrphan ? <option value={building}>{building}</option> : null}
      </select>
    </label>
  );
  const roomField = (
    <label className="field location-room">
      <span className="label">Room</span>
      <select
        value={room}
        onChange={(e) => chooseRoom(e.target.value)}
        disabled={disabled || loading || (!building && !roomOrphan)}
      >
        <option value="">—</option>
        {roomsForBuilding.map((r) => (
          <option key={r.code} value={r.code}>
            {r.label}
          </option>
        ))}
        {roomOrphan ? <option value={room}>{room}</option> : null}
      </select>
    </label>
  );

  // Stack: all three bare (each full width). Row: Campus + Building share a
  // 2-up row, Room spans the full width on its own line below.
  if (layout === "stack") {
    return (
      <>
        {campusField}
        {buildingField}
        {roomField}
      </>
    );
  }
  return (
    <>
      <div className="row">
        {campusField}
        {buildingField}
      </div>
      {roomField}
    </>
  );
}
